import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import {
  AddBluetoothDevice,
  DevicesStoreActionTypes,
  DevicesStoreNoAction,
  InitDevicesStore,
  RemoveAllDevices,
  RemoveBluetoothDevice,
  SetDevicesStore,
  UpdateConnectedDeviceInfo,
  UpdateRfidScanMode,
} from './devices-db.actions';
import { DevicesStoreFacade } from './devices-db.facade';
import { DEVICES_STORE_INITIAL_STATE } from './devices-db.reducer';
import produce from 'immer';
import { TNFCDevice, TPairedDevice, TRfidDevice } from '@monorepo/types';

@Injectable({
  providedIn: 'root',
})
export class DevicesStoreEffects {
  constructor(
    private action$: Actions,
    private devicesStoreFacade: DevicesStoreFacade,
    private persistanceStorage: PersistentStorageService,
  ) {}

  InitDevicesStore$ = createEffect(() => {
    return this.action$.pipe(
      ofType<InitDevicesStore>(DevicesStoreActionTypes.InitDevicesStore),
      switchMap((action) => {
        return from(this.persistanceStorage.get('[APP][INPUT_APP_DEVICES_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (res) return of(new SetDevicesStore(res, action.chainWithInitBleService));
            else {
              return from(
                this.persistanceStorage.set('[APP][INPUT_APP_DEVICES_DB]', DEVICES_STORE_INITIAL_STATE.devicesDB),
              ).pipe(
                take(1),
                map(() => {
                  return new SetDevicesStore(DEVICES_STORE_INITIAL_STATE.devicesDB, action.chainWithInitBleService);
                }),
              );
            }
          }),
        );
      }),
    );
  });

  SetDevicesStore$ = createEffect(() => {
    return this.action$.pipe(
      ofType<SetDevicesStore>(DevicesStoreActionTypes.SetDevicesStore),
      map((action) => {
        if (action.chainWithInitBleService)
          this.devicesStoreFacade.emitInitBleServiceSubscription(action.chainWithInitBleService);
        return new DevicesStoreNoAction();
      }),
    );
  });

  AddBluetoothDevice$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AddBluetoothDevice>(DevicesStoreActionTypes.AddBluetoothDevice),
      switchMap((action) => {
        return this.devicesStoreFacade.getDevicesDBFromCache().pipe(
          take(1),
          switchMap((devicesDB) => {
            const updatedDevicesDB = this.devicesStoreFacade.addBluetoothDevice(devicesDB, action.deviceInfo);
            return this.devicesStoreFacade.updateDevicesDBInCache(updatedDevicesDB).pipe(
              take(1),
              map(() => {
                return new SetDevicesStore(updatedDevicesDB);
              }),
            );
          }),
        );
      }),
    );
  });

  UpdateRfidScanMode$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateRfidScanMode>(DevicesStoreActionTypes.UpdateRfidScanMode),
      switchMap((action) =>
        this.devicesStoreFacade.getDevicesDBFromCache().pipe(
          take(1),
          switchMap((devicesDB) => {
            const updatedDevicesDB = produce(devicesDB, (draft) => {
              if (draft?.connectedDeviceInfo) {
                draft.connectedDeviceInfo.scanMode = action.newScanMode;
              }
            });
            return this.devicesStoreFacade.updateDevicesDBInCache(updatedDevicesDB).pipe(
              take(1),
              map(() => {
                return new SetDevicesStore(updatedDevicesDB);
              }),
            );
          }),
        ),
      ),
    ),
  );

  UpdateConnectedDeviceInfo$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateConnectedDeviceInfo>(DevicesStoreActionTypes.UpdateConnectedDeviceInfo),
      switchMap((action) => {
        return this.devicesStoreFacade.getDevicesDBFromCache().pipe(
          take(1),
          switchMap((devicesDB) => {
            const { connectionStatus, connectionType, deviceInfo } = action.params;
            const updatedDevicesDB = produce(devicesDB, (draft) => {
              if (connectionStatus === 'CONNECTED') {
                switch(connectionType) {
                  case 'BLUETOOTH': {
                    draft.connectedDeviceInfo.BLUETOOTH = deviceInfo as TPairedDevice;
                    break;
                  }
                  case 'RFID': {
                    draft.connectedDeviceInfo.RFID = deviceInfo as TRfidDevice;
                    break;
                  }
                  case 'NFC': {
                    draft.connectedDeviceInfo.NFC = deviceInfo as TNFCDevice;
                    break;
                  }
                }
              } else if (connectionStatus === 'DISCONNECTED') {
                switch(connectionType) {
                  case 'BLUETOOTH': {
                    draft.connectedDeviceInfo.BLUETOOTH = null;
                    break;
                  }
                  case 'RFID': {
                    draft.connectedDeviceInfo.RFID = null;
                    break;
                  }
                  case 'NFC': {
                    draft.connectedDeviceInfo.NFC = null;
                    break;
                  }
                }
              }
            });
            return this.devicesStoreFacade.updateDevicesDBInCache(updatedDevicesDB).pipe(
              take(1),
              map(() => {
                return new SetDevicesStore(updatedDevicesDB);
              }),
            );
          }),
        );
      }),
    ),
  );

  RemoveBluetoothDevice$ = createEffect(() => {
    return this.action$.pipe(
      ofType<RemoveBluetoothDevice>(DevicesStoreActionTypes.RemoveBluetoothDevice),
      switchMap((action) => {
        return this.devicesStoreFacade.getDevicesDBFromCache().pipe(
          take(1),
          switchMap((devicesDB) => {
            const updatedDevicesDB = this.devicesStoreFacade.removeDevice(devicesDB, action.deviceId);
            return this.devicesStoreFacade.updateDevicesDBInCache(updatedDevicesDB).pipe(
              take(1),
              map(() => {
                return new SetDevicesStore(updatedDevicesDB);
              }),
            );
          }),
        );
      }),
    );
  });

  RemoveAllDevices$ = createEffect(() => {
    return this.action$.pipe(
      ofType<RemoveAllDevices>(DevicesStoreActionTypes.RemoveAllDevices),
      switchMap((action) => {
        return this.devicesStoreFacade.getDevicesDBFromCache().pipe(
          take(1),
          switchMap((devicesDB) => {
            const updatedDevicesDB = this.devicesStoreFacade.removeAllDevices(devicesDB);
            return this.devicesStoreFacade.updateDevicesDBInCache(updatedDevicesDB).pipe(
              take(1),
              map(() => {
                return new SetDevicesStore(updatedDevicesDB);
              }),
            );
          }),
        );
      }),
    );
  });
}
